import { useMemo, useRef } from 'react';
import { AutoMessageType_Slug, Rule } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { AnimatePresence, motion } from 'motion/react';
import { AutoRulesQueries } from '@frontend/api-auto-rules';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  CheckboxField,
  ContentLoader,
  InlineNotification,
  MessageTemplate,
  TagType,
  useControlledField,
} from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks';
import { useScheduleRequestModalContext } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/Context/ScheduleRequestModalContext';
import { useScheduleRequestModalFormContext } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/Context/ScheduleRequestModalFormContext';
import { Card } from './Card';

type Props = {
  template: string;
  templateTags: TagType[];
  isReadOnlyTemplate: boolean;
  onTemplateChange: (template: string) => void;
};

export const ApprovalMessageCard = ({ template, templateTags, isReadOnlyTemplate, onTemplateChange }: Props) => {
  const { t } = useTranslation('scheduleCalendarRequest');
  const { selectedOrgId, selectedLocationIds } = useSchedulingLocationInfo();
  const { selectedScheduleRequest } = useScheduleRequestModalContext();
  const { shouldSendMessageV3, setShouldSendMessageV3 } = useScheduleRequestModalFormContext();
  const sendMessageDivRef = useRef<HTMLDivElement>(null);
  const isCheckboxToggled = useRef(false);

  const autoMessageListRulesQuery = AutoRulesQueries.useListRules<{ rules: Rule[] }>({
    orgId: selectedOrgId,
    businessGroups: selectedScheduleRequest.locationId ? [selectedScheduleRequest.locationId] : selectedLocationIds,
  });

  const hasSaveTheDateAutoMessageConfigured = useMemo(() => {
    return (autoMessageListRulesQuery.data?.rules || []).some(
      (rule) => rule.autoMessageType?.slug === AutoMessageType_Slug.SAVE_THE_DATE && rule.enabled === true
    );
  }, [autoMessageListRulesQuery.data]);

  const checkboxField = useControlledField({
    type: 'checkbox',
    value: shouldSendMessageV3,
    onChange: (value) => {
      isCheckboxToggled.current = true; // Mark that the checkbox has been toggled
      setShouldSendMessageV3(value);
    },
  });

  const cardTitle = (
    <>
      <CheckboxField name='send-approval-message-checkbox' label='' {...checkboxField} />
      {t('Send Approval Message')}
    </>
  );

  return (
    <Card title={cardTitle} disabled={!shouldSendMessageV3} css={{ gap: 0 }}>
      <ContentLoader show={autoMessageListRulesQuery.isLoading} />
      {hasSaveTheDateAutoMessageConfigured && (
        <InlineNotification
          message={t('The patient may already be receiving a save the date message')}
          size='small'
          status='warn'
          css={{ marginTop: theme.spacing(2), '> *': { flexGrow: 1 } }}
        />
      )}
      <AnimatePresence>
        {shouldSendMessageV3 && (
          <motion.div
            ref={sendMessageDivRef}
            css={{ overflow: 'hidden' }}
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: 'auto', opacity: 1, marginTop: theme.spacing(2) }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.5 }}
            onAnimationComplete={() =>
              isCheckboxToggled.current && sendMessageDivRef.current?.scrollIntoView({ behavior: 'smooth' })
            } // Ensure the card scrolls into view after animation
          >
            <MessageTemplate
              initialTemplate={template}
              tags={templateTags}
              draggableTags
              onChange={onTemplateChange}
              readOnly={isReadOnlyTemplate}
            >
              <MessageTemplate.Editor css={{ marginBottom: theme.spacing(1) }} />
              <MessageTemplate.TagList
                helperText={t('You can also drag and drop, or click a tag below, to add your message')}
              />
            </MessageTemplate>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};
